import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/utils-cart";
import { RoleBadge, StatusPill } from "@/components/Badges";
import { toast } from "sonner";

// Admin: Users + approvals
export function AdminUsers() {
  const [users, setUsers] = useState([]);
  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const approve = async (id, approved) => {
    await api.post(`/admin/users/${id}/approve`, { approved });
    toast.success(approved ? "Approved" : "Rejected");
    load();
  };

  return (
    <DashboardLayout title="Users & Approvals" subtitle="All accounts on the platform. Approve or reject signup applications.">
      <div className="v-card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="p-3">User</th>
              <th className="p-3">Roles</th>
              <th className="p-3">Approval</th>
              <th className="p-3">Joined</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-border last:border-0">
                <td className="p-3">
                  <div className="text-primary font-medium">{u.full_name}</div>
                  <div className="text-xs text-muted-foreground font-mono">{u.email}</div>
                </td>
                <td className="p-3 flex flex-wrap gap-1">{(u.roles || []).map((r) => <RoleBadge key={r} role={r} />)}</td>
                <td className="p-3"><StatusPill tone={u.approval_status === "approved" ? "success" : u.approval_status === "rejected" ? "danger" : "warning"}>{u.approval_status}</StatusPill></td>
                <td className="p-3 text-xs">{formatDate(u.created_at)}</td>
                <td className="p-3 text-right space-x-1">
                  <button onClick={() => approve(u.id, true)} className="v-btn-outline text-xs" data-testid={`admin-approve-${u.id}`}>Approve</button>
                  <button onClick={() => approve(u.id, false)} className="v-btn-ghost text-xs text-destructive" data-testid={`admin-reject-${u.id}`}>Reject</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}

// Admin: Payment Settings
export function AdminPayments() {
  const [s, setS] = useState(null);
  const load = () => api.get("/admin/payment-settings").then((r) => setS(r.data));
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    await api.patch("/admin/payment-settings", patch);
    toast.success("Settings saved");
    load();
  };

  if (!s) return <DashboardLayout title="Payment Settings"><div>Loading…</div></DashboardLayout>;

  return (
    <DashboardLayout title="Payment Settings" subtitle="Centralized Square + PayPal configuration for the entire platform.">
      <div className="grid lg:grid-cols-2 gap-4 max-w-5xl">
        <GatewayCard
          name="Square"
          enabled={s.square_enabled}
          env={s.square_environment}
          configured={s.square_configured}
          lastWebhook={s.last_square_webhook_at}
          onToggle={(v) => save({ square_enabled: v })}
          onEnv={(v) => save({ square_environment: v })}
        />
        <GatewayCard
          name="PayPal"
          enabled={s.paypal_enabled}
          env={s.paypal_environment}
          configured={s.paypal_configured}
          lastWebhook={s.last_paypal_webhook_at}
          onToggle={(v) => save({ paypal_enabled: v })}
          onEnv={(v) => save({ paypal_environment: v })}
        />
      </div>

      <div className="mt-6 v-card max-w-3xl">
        <h3 className="font-display text-lg font-semibold text-primary">Platform fees</h3>
        <div className="mt-4 grid sm:grid-cols-3 gap-3">
          <Row label="Currency">
            <select className="v-input" value={s.default_currency} onChange={(e) => save({ default_currency: e.target.value })} data-testid="admin-currency">
              {["CAD","USD","EUR","GBP"].map((c) => <option key={c}>{c}</option>)}
            </select>
          </Row>
          <Row label="Platform fee %">
            <input type="number" step="0.1" className="v-input" defaultValue={s.platform_fee_percent} onBlur={(e) => save({ platform_fee_percent: Number(e.target.value) })} data-testid="admin-fee" />
          </Row>
          <Row label="Payout hold (days)">
            <input type="number" className="v-input" defaultValue={s.payout_hold_days} onBlur={(e) => save({ payout_hold_days: Number(e.target.value) })} data-testid="admin-hold" />
          </Row>
        </div>
        <div className="mt-4">
          <label className="label-eyebrow mb-2 block">Refund policy</label>
          <textarea rows={3} className="v-input" defaultValue={s.refund_policy} onBlur={(e) => save({ refund_policy: e.target.value })} data-testid="admin-refund-policy" />
        </div>
      </div>
    </DashboardLayout>
  );
}

function GatewayCard({ name, enabled, env, configured, lastWebhook, onToggle, onEnv }) {
  const id = name.toLowerCase();
  return (
    <div className="v-card">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-semibold text-primary">{name}</h3>
        <StatusPill tone={configured ? "success" : "warning"}>{configured ? "Configured" : "Sandbox / mock"}</StatusPill>
      </div>
      <div className="mt-4 space-y-3 text-sm">
        <label className="flex items-center justify-between">
          <span>Enabled at checkout</span>
          <input type="checkbox" checked={enabled} onChange={(e) => onToggle(e.target.checked)} data-testid={`admin-${id}-enable`} />
        </label>
        <Row label="Environment">
          <select className="v-input" value={env} onChange={(e) => onEnv(e.target.value)} data-testid={`admin-${id}-env`}>
            <option value="sandbox">Sandbox</option>
            <option value="production">Production</option>
          </select>
        </Row>
        <Row label="Webhook status">
          <span className="font-mono text-xs">{lastWebhook ? `Last: ${formatDate(lastWebhook)}` : "No webhooks received yet"}</span>
        </Row>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return <div><div className="label-eyebrow mb-1">{label}</div>{children}</div>;
}

// Admin: FAQs & Guides
export function AdminContent() {
  const [faqs, setFaqs] = useState([]);
  const [guides, setGuides] = useState([]);
  const [tab, setTab] = useState("faqs");
  const [faq, setFaq] = useState({ question: "", answer: "", category: "general", audience: "all" });
  const [guide, setGuide] = useState({ title: "", body: "", role: "supplier", order: 0 });

  const load = () => {
    api.get("/faqs").then((r) => setFaqs(r.data));
    api.get("/guides").then((r) => setGuides(r.data));
  };
  useEffect(() => { load(); }, []);

  const createFaq = async (e) => { e.preventDefault(); await api.post("/admin/faqs", { ...faq, audience: [faq.audience] }); toast.success("FAQ added"); setFaq({ question: "", answer: "", category: "general", audience: "all" }); load(); };
  const delFaq = async (id) => { await api.delete(`/admin/faqs/${id}`); load(); };
  const createGuide = async (e) => { e.preventDefault(); await api.post("/admin/guides", { ...guide, order: Number(guide.order) }); toast.success("Guide added"); setGuide({ title: "", body: "", role: "supplier", order: 0 }); load(); };

  return (
    <DashboardLayout title="FAQ & Guides" subtitle="Content shown across the public site and role-specific guides.">
      <div className="flex gap-2 mb-4">
        <button onClick={() => setTab("faqs")} className={`v-btn-outline ${tab === "faqs" ? "border-primary bg-primary text-white" : ""}`} data-testid="admin-tab-faqs">FAQs ({faqs.length})</button>
        <button onClick={() => setTab("guides")} className={`v-btn-outline ${tab === "guides" ? "border-primary bg-primary text-white" : ""}`} data-testid="admin-tab-guides">Guides ({guides.length})</button>
      </div>

      {tab === "faqs" && (
        <>
          <form onSubmit={createFaq} className="v-card mb-4 space-y-3">
            <h3 className="font-display font-semibold text-primary">Add FAQ</h3>
            <input className="v-input" placeholder="Question" value={faq.question} onChange={(e) => setFaq({...faq, question: e.target.value})} required data-testid="faq-question" />
            <textarea rows={3} className="v-input" placeholder="Answer" value={faq.answer} onChange={(e) => setFaq({...faq, answer: e.target.value})} required data-testid="faq-answer" />
            <div className="grid sm:grid-cols-2 gap-2">
              <input className="v-input" placeholder="Category" value={faq.category} onChange={(e) => setFaq({...faq, category: e.target.value})} data-testid="faq-category" />
              <select className="v-input" value={faq.audience} onChange={(e) => setFaq({...faq, audience: e.target.value})} data-testid="faq-audience">
                {["all","supplier","merchant","affiliate","creator","fulfillment_partner","service_provider"].map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <button className="v-btn-primary" data-testid="faq-save">Save FAQ</button>
          </form>
          <div className="space-y-2">
            {faqs.map((f) => (
              <div key={f.id} className="v-card flex items-start justify-between gap-4">
                <div>
                  <div className="font-medium text-primary">{f.question}</div>
                  <div className="text-sm text-muted-foreground mt-1">{f.answer}</div>
                </div>
                <button onClick={() => delFaq(f.id)} className="v-btn-ghost text-destructive text-xs" data-testid={`faq-delete-${f.id}`}>Delete</button>
              </div>
            ))}
          </div>
        </>
      )}

      {tab === "guides" && (
        <>
          <form onSubmit={createGuide} className="v-card mb-4 space-y-3">
            <h3 className="font-display font-semibold text-primary">Add guide chapter</h3>
            <div className="grid sm:grid-cols-3 gap-2">
              <select className="v-input" value={guide.role} onChange={(e) => setGuide({...guide, role: e.target.value})} data-testid="guide-role">
                {["supplier","merchant","affiliate","creator","fulfillment_partner","service_provider"].map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              <input type="number" className="v-input" placeholder="Order" value={guide.order} onChange={(e) => setGuide({...guide, order: e.target.value})} data-testid="guide-order" />
              <input className="v-input sm:col-span-1" placeholder="Title" value={guide.title} onChange={(e) => setGuide({...guide, title: e.target.value})} required data-testid="guide-title" />
            </div>
            <textarea rows={5} className="v-input" placeholder="Guide body" value={guide.body} onChange={(e) => setGuide({...guide, body: e.target.value})} required data-testid="guide-body" />
            <button className="v-btn-primary" data-testid="guide-save">Add chapter</button>
          </form>
          <div className="space-y-2">
            {guides.map((g) => (
              <div key={g.id} className="v-card">
                <div className="font-mono text-xs text-muted-foreground">[{g.role}] · Order {g.order}</div>
                <div className="font-medium text-primary mt-1">{g.title}</div>
                <div className="text-sm text-muted-foreground mt-1">{g.body}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </DashboardLayout>
  );
}

// Admin: System Health
export function AdminHealth() {
  const [h, setH] = useState(null);
  useEffect(() => { api.get("/system/health").then((r) => setH(r.data)); }, []);
  if (!h) return <DashboardLayout title="System Health"><div>Loading…</div></DashboardLayout>;
  return (
    <DashboardLayout title="System Health" subtitle="Live status of integrations, storage, and platform configuration.">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-4xl">
        <Health label="Overall" status={h.status === "ok"} val={h.status} />
        <Health label="MongoDB" status={h.mongo} val={h.mongo ? "connected" : "down"} />
        <Health label="Claude AI" status={h.ai_configured} val={h.ai_configured ? "configured" : "not configured"} />
        <Health label="Square" status={h.square_configured} val={h.square_configured ? "configured" : "sandbox / mock"} />
        <Health label="PayPal" status={h.paypal_configured} val={h.paypal_configured ? "configured" : "sandbox / mock"} />
        <Health label="Currency" status={true} val={h.currency} />
        <Health label="Platform fee" status={true} val={`${h.platform_fee_percent}%`} />
        <Health label="API Version" status={true} val={h.version} />
      </div>
    </DashboardLayout>
  );
}

function Health({ label, status, val }) {
  return (
    <div className="v-card">
      <div className="flex items-center justify-between">
        <div className="label-eyebrow">{label}</div>
        <span className={`w-2 h-2 rounded-full ${status ? "bg-accent-green" : "bg-amber-500"}`} />
      </div>
      <div className="mt-2 font-mono text-sm">{val}</div>
    </div>
  );
}
